import { type Observable, ApolloLink, type FetchResult } from "@apollo/client";
import { addBreadcrumb } from "@sentry/react";
import type { SeverityLevel } from "@sentry/types";

import { updateShouldFrontendRefresh } from "./shouldFrontendRefresh.store";
type FetchResultWithShouldFrontendRefresh = FetchResult<Record<string, any>, Record<string, any>>;

const updateFrontendRefresh = (shouldFrontendRefresh: boolean) => {
  updateShouldFrontendRefresh(shouldFrontendRefresh);

  if (shouldFrontendRefresh) {
    // We don't have direct access to Link components in @fullscript/aviary-web
    // So we can't directly set reloadDocument to true
    // Dispatch event that is listened to within aviary-web so that we set reloadDocument properly
    const shouldFrontendRefreshEvent = new CustomEvent("shouldFrontendRefresh");
    window.dispatchEvent(shouldFrontendRefreshEvent);
  }
};

/**
 * Apollo link that listens for calls from the backend to force refresh the frontend on next page navigation
 * If the backend responds with shouldFrontendRefresh `true`, we set a global variable to signal other parts of the codebase to refresh on the next navigation
 */
const getShouldFrontendRefreshLink = () => {
  return new ApolloLink((operation, forward) => {
    return (forward(operation) as Observable<FetchResultWithShouldFrontendRefresh>).map(result => {
      const shouldFrontendRefresh = result?.extensions?.shouldFrontendRefresh ?? false;
      updateFrontendRefresh(shouldFrontendRefresh);

      if (shouldFrontendRefresh) {
        addBreadcrumb({
          category: "shouldFrontendRefresh",
          data: {
            shouldFrontendRefresh,
          },
          level: "info" as SeverityLevel,
        });
      }

      return result;
    });
  });
};

export { getShouldFrontendRefreshLink };
